import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useAutenticacao } from '../../context/autenticacao_contexto';
import { cores } from '../../theme';
import { storage } from '../../firebase/config';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';

import {
    Typography, Container, Box, Paper, TextField, Button, Alert,
    IconButton, Avatar, Fade, Snackbar, useTheme, LinearProgress,
    Grid, ImageList, ImageListItem, Chip
} from '@mui/material';
import {
    ArrowBack, Save as SaveIcon, Assignment as AssignmentIcon,
    Upload as UploadIcon, Image as ImageIcon, Close as CloseIcon
} from '@mui/icons-material';

const CasoCriarPagina = () => {
    const navegar = useNavigate();
    const { perfilUsuario } = useAutenticacao();
    const theme = useTheme();
    const [titulo, setTitulo] = useState('');
    const [descricao, setDescricao] = useState('');
    const [erro, setErro] = useState('');
    const [sucesso, setSucesso] = useState('');
    const [arquivos, setArquivos] = useState([]);
    const [progresso, setProgresso] = useState(0);
    const [processando, setProcessando] = useState(false);

    const lidarComSelecaoDeArquivos = (e) => {
        const arquivosSelecionados = Array.from(e.target.files);
        setArquivos(prevArquivos => [...prevArquivos, ...arquivosSelecionados]);
    };

    const removerArquivo = (index) => {
        setArquivos(prevArquivos => prevArquivos.filter((_, i) => i !== index));
    };

    const lidarComSubmissao = async (e) => {
        e.preventDefault();
        setErro('');
        setSucesso('');

        if (!titulo) {
            setErro('O campo "Título" é obrigatório.');
            return;
        }

        setProcessando(true);

        try {
            let urlsDasImagens = [];
            if (arquivos.length > 0) {
                const uploadPromises = arquivos.map(arquivo => {
                    return new Promise((resolve, reject) => {
                        const nomeArquivo = `${Date.now()}-${arquivo.name}`;
                        const storageRef = ref(storage, `casos-clinicos/${nomeArquivo}`);
                        const uploadTask = uploadBytesResumable(storageRef, arquivo);

                        uploadTask.on('state_changed',
                            (snapshot) => {
                                const prog = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                                setProgresso(prog);
                            },
                            (error) => {
                                console.error("Erro no upload:", error);
                                reject(error);
                            },
                            async () => {
                                const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                                resolve(downloadURL);
                            }
                        );
                    });
                });

                urlsDasImagens = await Promise.all(uploadPromises);
            }

            const dadosDoCaso = {
                titulo,
                descricao,
                criado_por_usuario_id: perfilUsuario.id,
                imagem_urls: urlsDasImagens,
            };

            await api.post('/api/casos-clinicos/criar', dadosDoCaso);
            setSucesso('Caso Clínico criado com sucesso!');

            setTimeout(() => {
                navegar('/gerenciar-casos');
            }, 1500);

        } catch (err) {
            setErro(err.response?.data?.erro || 'Ocorreu um erro ao criar o caso clínico.');
        } finally {
            setProcessando(false);
        }
    };

    return (
        <>
            <Snackbar
                open={!!sucesso}
                autoHideDuration={4000}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    severity="success"
                    variant="filled"
                    sx={{
                        width: '100%',
                        boxShadow: cores.sombras.medio,
                        backgroundColor: cores.sucesso.principal
                    }}
                >
                    {sucesso}
                </Alert>
            </Snackbar>

            <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 4 } }}>
                <Fade in={true} timeout={500}>
                    <Paper sx={{ p: { xs: 2, sm: 4 }, borderRadius: 3, boxShadow: cores.sombras.medio }}>
                        <Box component="form" onSubmit={lidarComSubmissao}>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: 4,
                                p: { xs: 2, sm: 3 },
                                borderRadius: 2,
                                background: cores.gradientes.primario,
                                color: 'white'
                            }}>
                                <IconButton
                                    onClick={() => navegar('/gerenciar-casos')}
                                    sx={{
                                        mr: 2,
                                        color: 'white',
                                        bgcolor: 'rgba(255,255,255,0.2)',
                                        '&:hover': {
                                            bgcolor: 'rgba(255,255,255,0.3)',
                                        }
                                    }}
                                >
                                    <ArrowBack />
                                </IconButton>
                                <Box sx={{ flex: 1 }}>
                                    <Typography
                                        variant="h4"
                                        component="h1"
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                                            lineHeight: 1.2
                                        }}
                                    >
                                        Criar Novo Caso Clínico
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            opacity: 0.9,
                                            fontSize: { xs: '0.9rem', sm: '1rem' }
                                        }}
                                    >
                                        Adicione um novo caso para avaliação dos alunos
                                    </Typography>
                                </Box>
                            </Box>

                            <Fade in={true} timeout={600}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: { xs: 2, sm: 3 },
                                        mb: 3,
                                        bgcolor: '#f8fafc',
                                        borderRadius: 2,
                                        border: `1px solid ${cores.cinza.claro}`,
                                        boxShadow: cores.sombras.suave
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                        <Avatar
                                            sx={{
                                                mr: 2,
                                                bgcolor: `rgba(${parseInt(cores.primaria.principal.slice(1, 3), 16)}, ${parseInt(cores.primaria.principal.slice(3, 5), 16)}, ${parseInt(cores.primaria.principal.slice(5, 7), 16)}, 0.1)`,
                                                color: cores.primaria.principal
                                            }}
                                        >
                                            <AssignmentIcon />
                                        </Avatar>
                                        <Box>
                                            <Typography variant="overline" sx={{ color: cores.cinza.medio, fontWeight: 600 }}>
                                                INFORMAÇÕES DO CASO
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: cores.cinza.escuro }}>
                                                Preencha os detalhes básicos do caso clínico e adicione imagens
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Paper>
                            </Fade>

                            <Box sx={{ p: { xs: 0, sm: 2 } }}>
                                <TextField
                                    label="Título do Caso Clínico"
                                    variant="outlined"
                                    fullWidth
                                    required
                                    value={titulo}
                                    onChange={(e) => setTitulo(e.target.value)}
                                    sx={{ mb: 3 }}
                                    InputProps={{
                                        sx: { borderRadius: 2 }
                                    }}
                                    helperText="Exemplo: Diagnóstico de paciente com dor abdominal"
                                />

                                <TextField
                                    label="Descrição Completa do Caso"
                                    variant="outlined"
                                    fullWidth
                                    multiline
                                    rows={10}
                                    value={descricao}
                                    onChange={(e) => setDescricao(e.target.value)}
                                    InputProps={{
                                        sx: { borderRadius: 2 }
                                    }}
                                    helperText="Descreva em detalhes o caso clínico, incluindo histórico, sintomas e exames relevantes"
                                />

                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 3,
                                        mt: 3,
                                        borderRadius: 2,
                                        border: `1px dashed ${cores.cinza.medio}`,
                                        backgroundColor: 'rgba(241, 245, 249, 0.6)',
                                    }}
                                >
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        flexDirection: { xs: 'column', sm: 'row' },
                                        gap: 2
                                    }}>
                                        <Avatar
                                            sx={{
                                                bgcolor: `rgba(${parseInt(cores.secundaria.principal.slice(1, 3), 16)}, ${parseInt(cores.secundaria.principal.slice(3, 5), 16)}, ${parseInt(cores.secundaria.principal.slice(5, 7), 16)}, 0.1)`,
                                                color: cores.secundaria.principal
                                            }}
                                        >
                                            <ImageIcon />
                                        </Avatar>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: cores.secundaria.principal }}>
                                                Imagens do Caso Clínico
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: cores.cinza.escuro, mb: 2 }}>
                                                Adicione imagens como exames, radiografias ou ilustrações relevantes para o caso
                                            </Typography>

                                            <Button
                                                variant="outlined"
                                                component="label"
                                                startIcon={<UploadIcon />}
                                                sx={{
                                                    borderRadius: 2,
                                                    borderColor: cores.secundaria.principal,
                                                    color: cores.secundaria.principal,
                                                    '&:hover': {
                                                        borderColor: cores.secundaria.clara,
                                                        bgcolor: `rgba(${parseInt(cores.secundaria.principal.slice(1, 3), 16)}, ${parseInt(cores.secundaria.principal.slice(3, 5), 16)}, ${parseInt(cores.secundaria.principal.slice(5, 7), 16)}, 0.04)`
                                                    }
                                                }}
                                            >
                                                Selecionar Imagens
                                                <input type="file" hidden multiple accept="image/*" onChange={lidarComSelecaoDeArquivos} />
                                            </Button>
                                            {arquivos.length > 0 && (
                                                <Chip
                                                    label={`${arquivos.length} ${arquivos.length === 1 ? 'imagem selecionada' : 'imagens selecionadas'}`}
                                                    sx={{
                                                        ml: 1,
                                                        bgcolor: `rgba(${parseInt(cores.secundaria.principal.slice(1, 3), 16)}, ${parseInt(cores.secundaria.principal.slice(3, 5), 16)}, ${parseInt(cores.secundaria.principal.slice(5, 7), 16)}, 0.1)`,
                                                        color: cores.secundaria.principal,
                                                        fontWeight: 500
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    </Box>

                                    {arquivos.length > 0 && (
                                        <Box sx={{ mt: 3 }}>
                                            <ImageList cols={arquivos.length > 3 ? 4 : arquivos.length} gap={8}>
                                                {arquivos.map((arquivo, index) => (
                                                    <ImageListItem key={index} sx={{ position: 'relative' }}>
                                                        <img
                                                            src={URL.createObjectURL(arquivo)}
                                                            alt={`Imagem ${index + 1}`}
                                                            loading="lazy"
                                                            style={{
                                                                borderRadius: 8,
                                                                height: 140,
                                                                objectFit: 'cover',
                                                                border: `1px solid ${cores.cinza.claro}`
                                                            }}
                                                        />
                                                        <IconButton
                                                            onClick={() => removerArquivo(index)}
                                                            sx={{
                                                                position: 'absolute',
                                                                top: 4,
                                                                right: 4,
                                                                bgcolor: 'rgba(255, 255, 255, 0.9)',
                                                                color: cores.erro.principal,
                                                                width: 24,
                                                                height: 24,
                                                                '&:hover': {
                                                                    bgcolor: 'rgba(255, 255, 255, 1)',
                                                                }
                                                            }}
                                                        >
                                                            <CloseIcon sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                position: 'absolute',
                                                                bottom: 0,
                                                                left: 0,
                                                                right: 0,
                                                                bgcolor: 'rgba(0, 0, 0, 0.6)',
                                                                color: 'white',
                                                                p: 0.5,
                                                                textAlign: 'center',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                                borderBottomLeftRadius: 8,
                                                                borderBottomRightRadius: 8
                                                            }}
                                                        >
                                                            {arquivo.name}
                                                        </Typography>
                                                    </ImageListItem>
                                                ))}
                                            </ImageList>
                                        </Box>
                                    )}
                                </Paper>

                                {processando && (
                                    <Box sx={{ mt: 3 }}>
                                        <Typography variant="body2" sx={{ mb: 1, color: cores.cinza.escuro }}>
                                            Enviando imagens... {progresso.toFixed(0)}%
                                        </Typography>
                                        <LinearProgress
                                            variant="determinate"
                                            value={progresso}
                                            sx={{
                                                height: 8,
                                                borderRadius: 4,
                                                bgcolor: `rgba(${parseInt(cores.primaria.principal.slice(1, 3), 16)}, ${parseInt(cores.primaria.principal.slice(3, 5), 16)}, ${parseInt(cores.primaria.principal.slice(5, 7), 16)}, 0.1)`,
                                                '& .MuiLinearProgress-bar': {
                                                    bgcolor: cores.primaria.principal
                                                }
                                            }}
                                        />
                                    </Box>
                                )}

                                {erro && (
                                    <Alert
                                        severity="error"
                                        sx={{
                                            mt: 2,
                                            borderRadius: 2,
                                            backgroundColor: cores.erro.claro,
                                            borderColor: cores.erro.medio,
                                            color: cores.erro.escuro,
                                            '& .MuiAlert-icon': {
                                                alignItems: 'center'
                                            }
                                        }}
                                    >
                                        {erro}
                                    </Alert>
                                )}

                                <Box sx={{
                                    mt: 4,
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    gap: 2,
                                    flexDirection: { xs: 'column', sm: 'row' }
                                }}>
                                    <Button
                                        variant="outlined"
                                        onClick={() => navegar('/gerenciar-casos')}
                                        sx={{
                                            borderRadius: 2,
                                            py: 1.5,
                                            px: 3,
                                            borderColor: cores.cinza.medio,
                                            color: cores.cinza.escuro,
                                            '&:hover': {
                                                borderColor: cores.cinza.escuro,
                                                bgcolor: 'rgba(71, 85, 105, 0.04)'
                                            }
                                        }}
                                    >
                                        Cancelar
                                    </Button>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        startIcon={<SaveIcon />}
                                        disabled={processando}
                                        sx={{
                                            borderRadius: 2,
                                            py: 1.5,
                                            px: 3,
                                            bgcolor: cores.primaria.principal,
                                            fontWeight: 600,
                                            boxShadow: cores.sombras.suave,
                                            '&:hover': {
                                                bgcolor: cores.primaria.escuro,
                                            }
                                        }}
                                    >
                                        {processando ? 'Enviando...' : 'Salvar Caso Clínico'}
                                    </Button>
                                </Box>
                            </Box>
                        </Box>
                    </Paper>
                </Fade>
            </Container>
        </>
    );
};

export default CasoCriarPagina;